import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Row, Table, Button, Col } from "reactstrap";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  ColumnDef,
  Table as ReactTableInstance,
  Column,
  FilterFn,
  ColumnFiltersState,
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";

// ========== Types ==========
type TableContainerProps<T> = {
  columns: ColumnDef<T, any>[];
  data: T[];
  tableClass?: string;
  theadClass?: string;
  divClassName?: string;
  isBordered?: boolean;
  isPagination?: boolean;
  isGlobalFilter?: boolean;
  isAddButton?: boolean;
  handleUserClick?: () => void;
  isCustomPageSize?: boolean;
  buttonClass?: string;
  buttonName?: string;
  paginationWrapper?: string;
  paginationClass?: string;
  SearchPlaceholder?: string;
};

// ========== Debounced Input ==========
interface DebouncedInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  value: string;
  onChange: (value: string) => void;
  debounce?: number;
}


const DebouncedInput: React.FC<DebouncedInputProps> = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);
    return () => clearTimeout(timeout);
  }, [value, debounce, onChange]);

  return (
    <Col sm={4}>
      <input
        {...props}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className={`form-control ${props.className || ""}`}
      />
    </Col>
  );
};

// ========== Per-Column Filter ==========
type FilterProps<T> = {
  column: Column<T, unknown>;
};


const FilterAdvance: React.FC<FilterProps<any>> = ({ column }) => {
  const columnFilterValue = column.getFilterValue() as string;

  const memoizedInput = useMemo(() => (
    <DebouncedInput
      value={columnFilterValue ?? ""}
      onChange={(value) => column.setFilterValue(value)}
      placeholder="Search..."
      className="w-100"
    />
  ), [columnFilterValue, column]);

  return memoizedInput;
};


// ========== TableContainer ==========
function TableContainer<T>({
  columns,
  data,
  tableClass,
  theadClass,
  divClassName = "table-responsive",
  isBordered = false,
  isPagination = true,
  isGlobalFilter = false,
  isAddButton = false,
  handleUserClick,
  isCustomPageSize = true,
  buttonClass,
  buttonName,
  paginationWrapper = "",
  paginationClass = "",
  SearchPlaceholder = "Search...",
}: TableContainerProps<T>) {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");

  const fuzzyFilter: FilterFn<T> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta?.({ itemRank });
    return itemRank.passed;
  };

  const table = useReactTable<T>({
    data,
    columns,
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const {
    getHeaderGroups,
    getRowModel,
    getState,
    setPageIndex,
    nextPage,
    previousPage,
    getPageOptions,
    getCanNextPage,
    getCanPreviousPage,
  } = table;

  return (
    <Fragment>
      <Row className="mb-2">
        {isCustomPageSize && (
          <Col sm={2}>
            <select
              className="form-select pageSize mb-2"
              value={getState().pagination.pageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}
            >
              {[2,10, 20, 30, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </Col>
        )}

        {isGlobalFilter && (
          <DebouncedInput
            value={globalFilter ?? ""}
            onChange={(value) => setGlobalFilter(String(value))}
            className="form-control search-box me-2 mb-2 d-inline-block"
            placeholder={SearchPlaceholder}
          />
        )}

        {isAddButton && (
          <Col sm={6}>
            <div className="text-sm-end">
              <Button
                type="button"
                className={buttonClass}
                onClick={handleUserClick}
              >
                <i className="mdi mdi-plus me-1"></i> {buttonName}
              </Button>
            </div>
          </Col>
        )}
      </Row>

      <div className={divClassName}>
        <Table hover className={tableClass} bordered={isBordered}>
          <thead className={theadClass}>
            {getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className={
                      header.column.getIsSorted()
                        ? header.column.getIsSorted() === "asc"
                          ? "sorting sorting_asc"
                          : "sorting sorting_desc"
                        : header.column.getCanSort()
                        ? "sorting"
                        : ""
                    }
                  >
                    {header.isPlaceholder ? null : (
                      <>
                        <div
                          className={
                            header.column.getCanSort()
                              ? "cursor-pointer select-none"
                              : ""
                          }
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {header.column.getIsSorted() === "asc"
                            ? " 🔼"
                            : header.column.getIsSorted() === "desc"
                            ? " 🔽"
                            : ""}
                        </div>
                        {/* Here is problem */}
                        {/* {header.column.getCanFilter() && (
                          <FilterAdvance column={header.column} />
                        )} */}
                      </>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {isPagination && (
        <Row>
          <Col sm={12} md={5}>
            <div className="dataTables_info">
              Showing {getState().pagination.pageSize} of {data.length} Results
            </div>
          </Col>
          <Col sm={12} md={7}>
            <div className={paginationWrapper}>
              <ul className={paginationClass}>
                <li
                  className={`paginate_button page-item previous ${
                    !getCanPreviousPage() ? "disabled" : ""
                  }`}
                >
                  <button className="page-link" onClick={previousPage}>
                    <i className="mdi mdi-chevron-left"></i>
                  </button>
                </li>

                {getPageOptions().map((page, index) => (
                  <li
                    key={index}
                    className={`paginate_button page-item ${
                      getState().pagination.pageIndex === page ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setPageIndex(page)}
                    >
                      {page + 1}
                    </button>
                  </li>
                ))}

                <li
                  className={`paginate_button page-item next ${
                    !getCanNextPage() ? "disabled" : ""
                  }`}
                >
                  <button className="page-link" onClick={nextPage}>
                    <i className="mdi mdi-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </div>
          </Col>
        </Row>
      )}
    </Fragment>
  );
}

export default TableContainer;
